import { useMemo } from "react";

import { isVoiceTask, useActiveTask } from "@/sdk";

/**
 * Resolves the active task into a live voice task for the workspace.
 *
 * `activeVoiceTask` is the active task when it's a voice task, else null. `hasLiveVoiceTask` is true only
 * when the caller reports an active voice task AND it isn't in a terminal state — stale `completed` /
 * `cancelled` tasks would otherwise keep the call bar open (showing the customer's MDN) after the call
 * ended. `hasActiveVoiceTask` is passed in (rather than re-derived) to preserve the caller's gate.
 */
export function useLiveVoiceTask(hasActiveVoiceTask: boolean) {
    const activeTask = useActiveTask();
    const activeVoiceTask = useMemo(() => (isVoiceTask(activeTask) ? activeTask : null), [activeTask]);

    const hasLiveVoiceTask =
        hasActiveVoiceTask &&
        activeVoiceTask !== null &&
        activeVoiceTask.status !== "completed" &&
        activeVoiceTask.status !== "cancelled";

    return useMemo(() => ({ activeVoiceTask, hasLiveVoiceTask }), [activeVoiceTask, hasLiveVoiceTask]);
}
